// CRM 2.0 Agreement KYC (M3-F3 / F5 / F7; rows M3.3, M3.18, M3.22, M3.35, M3.39, M3.51; DC-76 / DC-86 / DC-112 /
// DC-145 / DC-173). Opens today's Agreement KYC form with the CRM 2.0 save:
//   - every new file goes through the server into private storage (crm2doc:<id>/<name>), never a public link
//   - edit: "Review changes" (each change, old → new, and what happens next) before Save (DC-76)
//   - one server submit (AgreementForm + applicants + bank in one transaction); the Booking Form is filled
//     from it; a new review draft revision follows when something changed
//   - every submit shows a result, also when nothing changed (DC-112); with the discount checks (M4-F4 / M3-F8)
//     the result is the discount pop-up flow (Crm2DiscountModal DiscountFlow)
// mode: "new" · "edit" · "correct" (Final Review sent the KYC back, M3-F7)
import React, { useEffect, useRef, useState } from "react";
import { Box, Modal } from "@mui/material";
import AgreementKYC from "../../../Modals/AgreementKYC/AgreementKYC";
import Close from "../../Icons/Close";
import { uploadKycFile, getCrm2Kyc, submitCrm2Kyc, isCrm2DocRef } from "../../../Services/Crm2Portal";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import { errorTost, successTost } from "../../../Services/Toast";
import css from "./Crm2Booking.module.css";
import useConsentPoint from "./TermsGate";
import { DiscountFlow } from "./Crm2DiscountModal";

// [object path in the applicant payload, document kind]
const FILES = [
	["applicant.aadharFileUrl.url1", "AADHAAR_FRONT", "Aadhaar front"],
	["applicant.aadharFileUrl.url2", "AADHAAR_BACK", "Aadhaar back"],
	["applicant.panFileUrl.url", "PAN", "PAN card"],
	["applicant.loanSanctionUrl.url", "LOAN_SANCTION", "Loan sanction letter"],
	["POA.aadharFileUrl.url1", "POA_AADHAAR_FRONT", "POA Aadhaar front"],
	["POA.aadharFileUrl.url2", "POA_AADHAAR_BACK", "POA Aadhaar back"],
	["POA.panFileUrl.url", "POA_PAN", "POA PAN card"],
];
const FIELDS = [
	["salutation", "Title"],
	["firstName", "First name"],
	["middleName", "Middle name"],
	["lastName", "Last name"],
	["gender", "Gender"],
	["occupation", "Occupation"],
	["contactNumber", "Mobile"],
	["email", "Email"],
	["panNumber", "PAN"],
	["aadharNumber", "Aadhaar"],
	["addressLine1", "Address line 1"],
	["addressLine2", "Address line 2"],
	["addressLine3", "Address line 3"],
	["pincode", "Pincode"],
	["city", "City"],
	["state", "State"],
	["country", "Country"],
];
const BANK = [
	["accHolderName", "Account holder"],
	["accountNo", "Account number"],
	["ifscCode", "IFSC"],
	["bankName", "Bank"],
	["accType", "Account type"],
];

const getAt = (o, path) => path.split(".").reduce((x, k) => (x ? x[k] : undefined), o);
const setAt = (o, path, v) => {
	const keys = path.split(".");
	let x = o;
	for (const k of keys.slice(0, -1)) {
		if (!x[k] || typeof x[k] !== "object") x[k] = {};
		x = x[k];
	}
	x[keys[keys.length - 1]] = v;
};
const clone = (o) => {
	// files (File objects) must stay as they are
	if (o instanceof File || o instanceof Blob) return o;
	if (Array.isArray(o)) return o.map(clone);
	if (o && typeof o === "object") return Object.fromEntries(Object.entries(o).map(([k, v]) => [k, clone(v)]));
	return o;
};
const nameOf = (a) => [a?.firstName, a?.lastName].filter(Boolean).join(" ");
const show = (v) => (v === undefined || v === null || v === "" ? "—" : String(v));

// Review changes (DC-76): what changed against the last saved KYC
function diff(saved, applicants, bank) {
	const out = [];
	if (!saved) return out;
	const prev = saved.applicants || [];
	applicants.forEach((app, i) => {
		const old = prev.find((p) => p.applicantId && p.applicantId === app.applicantId);
		const who = `Applicant ${i + 1}`;
		if (!old) return out.push([who, "—", `${nameOf(app.applicant)} (added)`]);
		if (i === 0 && old.type !== "first") out.push(["Primary applicant", nameOf(prev[0]?.applicant), nameOf(app.applicant)]);
		for (const [k, label] of FIELDS) if (String(old.applicant?.[k] ?? "") !== String(app.applicant?.[k] ?? "")) out.push([`${who} · ${label}`, show(old.applicant?.[k]), show(app.applicant?.[k])]);
		for (const [path, , label] of FILES) {
			const v = getAt(app, path);
			if (v instanceof File || v instanceof Blob) out.push([`${who} · ${label}`, getAt(old, path) ? "file" : "—", "new file"]);
		}
	});
	prev.filter((p) => !applicants.some((a) => a.applicantId === p.applicantId)).forEach((p) => out.push(["Applicant", nameOf(p.applicant), "removed"]));
	const ob = saved.form?.customerBankDetails || {};
	for (const [k, label] of BANK) if (String(ob[k] ?? "") !== String(bank?.[k] ?? "")) out.push([`Bank · ${label}`, show(ob[k]), show(bank?.[k])]);
	return out;
}

export default function Crm2KycModal({ allotment, card, mode = "new", onClose, onDone }) {
	const apId = allotment.allotmentPaymentId;
	const [saved, setSaved] = useState(null);
	const [review, setReview] = useState(null); // { changes, run }
	const [result, setResult] = useState(null);
	const [forceEdit, setForceEdit] = useState(false); // "Add them back" from the discount result reopens the form
	const pending = useRef(null);
	const { requirePoint, termsModal } = useConsentPoint();
	const edit = forceEdit || mode !== "new" || !!card?.kycFilled;
	const correction = card?.kyc?.correction?.reason || null;

	useEffect(() => {
		if (!edit) return;
		getCrm2Kyc(apId)
			.then(setSaved)
			.catch((err) => console.warn("[crm2][kyc] saved KYC not read", err.message));
	}, [apId, edit, forceEdit]);

	// uploads every new file, then one server submit
	const run = async (s) => {
		presentLoader();
		try {
			const applicants = s.applicantsData.map((a, i) => ({
				applicantId: s.agreementKYCs[i]?.applicantId || null,
				applicant: clone(a.applicant),
				POA: a.POA ? clone(a.POA) : null,
			}));
			for (let i = 0; i < applicants.length; i += 1) {
				for (const [path, kind] of FILES) {
					const v = getAt(applicants[i], path);
					if (v instanceof File || v instanceof Blob) {
						const up = await uploadKycFile(apId, kind, i + 1, v);
						setAt(applicants[i], path, up.ref);
					} else if (typeof v === "string" && v && !isCrm2DocRef(v)) {
						throw new Error("Please upload your files again (they now go to VJ's secure storage).");
					}
				}
			}
			const bankLoan = s.value === "yes" && s.selfFundedValue === "bank";
			const body = {
				form: {
					isSelfFunded: s.value === "no",
					bankType: s.value === "yes" ? s.selfFundedValue : null,
					bankId: bankLoan && s.loanData && s.loanData !== "Other" ? s.loanData : null,
					otherBank: bankLoan && s.loanData === "Other" ? s.otherBank : null,
					loanDetails: { ...(s.bankerDetails || {}), loanSource: s.value === "yes" ? s.loanSource : null },
					customerBankDetails: s.bank,
				},
				applicants,
				removedApplicantIds: s.deleteUsers || [],
			};
			const res = await submitCrm2Kyc(apId, body);
			console.log("[crm2][kyc] submitted", { kycVersion: res?.kycVersion, changes: res?.changes?.length, review: res?.reviewDraft });
			dismissLoader();
			setResult(res);
		} catch (err) {
			dismissLoader();
			console.warn("[crm2][kyc] submit failed", err.message, err.fieldErrors || "");
			errorTost(err.message || "Could not save your Agreement KYC. Please try again.");
		}
	};

	const submit = async (s) => {
		if (!edit || !saved) return run(s);
		dismissLoader();
		const changes = diff(saved, s.applicantsData.map((a, i) => ({ ...a, applicantId: s.agreementKYCs[i]?.applicantId || null })), s.bank);
		pending.current = s;
		setReview({ changes });
	};

	const topNote = correction ? (
		<div className={css.badBox} style={{ margin: "1.2rem 2.4rem 0" }}>
			<b>Your CRM asked you to correct your KYC:</b> {correction} Change it below and press Submit; then confirm the new review draft by OTP. Your plan, parking and the stamp duty &amp; charges you paid stay as they are.
		</div>
	) : edit ? (
		<div className={css.info} style={{ margin: "1.2rem 2.4rem 0" }}>
			<b>You can change every detail and add, remove or make another applicant primary</b> until your CRM approves the Final Review. Each saved change makes a new review draft revision; every applicant confirms it again by OTP, and the Final Review waits for it.
		</div>
	) : null;

	return (
		<>
			{!result && (
				<AgreementKYC
					open
					handleClose={onClose}
					allotment={allotment}
					edit={edit}
					crm2={{
						submit,
						topNote,
						// M3-F11 / M3.39: Legal's terms on kyc_submit / kyc_correct; today's KYC terms text while none are Live
						beforeSave: (save, showTodayTerms) => requirePoint(correction ? "kyc_correct" : "kyc_submit", apId, save, { fallback: showTodayTerms }),
					}}
				/>
			)}
			{termsModal}
			{review && (
				<Modal open onClose={() => setReview(null)} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
					<Box className={css.modal}>
						<div className={css.modalHeader}>
							<span>Review changes</span>
							<span className={css.modalClose} onClick={() => setReview(null)} tabIndex={0}>
								<Close width={"1.2rem"} height={"1.2rem"} />
							</span>
						</div>
						<div className={css.modalContent}>
							{review.changes.length ? (
								<div className={css.chgList}>
									{review.changes.map(([f, o, n], i) => (
										<div key={i}>
											<b>{f}</b>
											<span>
												{o} → {n}
											</span>
										</div>
									))}
								</div>
							) : (
								<p className={css.muted}>No changes yet. Submit again to re-run the loyalty and female 1% checks.</p>
							)}
							<div className={css.info}>
								{review.changes.length
									? `What happens next: we make review draft Revision ${(card?.reviewDraft?.revisionNo || 0) + 1}; every applicant confirms it again by OTP; your Final Review waits for it.`
									: "Nothing changes in your review draft."}
							</div>
						</div>
						<div className={css.modalFooter}>
							<button type="button" className={css.btn} onClick={() => setReview(null)}>
								Go back
							</button>
							<button
								type="button"
								className={`${css.btn} ${css.btnP}`}
								onClick={() => {
									setReview(null);
									run(pending.current);
								}}
							>
								{review.changes.length ? (correction ? "Submit correction" : "Save") : "Submit again"}
							</button>
						</div>
					</Box>
				</Modal>
			)}
			{result && result.discountPreview && !result.discountPreview.error && !result.discountPreview.locked && (
				<DiscountFlow
					apId={apId}
					card={{ ...card, allotment }}
					view={result.discountPreview}
					kyc={{ ...result, edit }}
					onClose={() => onDone()}
					onDone={() => {
						successTost("Agreement KYC saved.");
						onDone();
					}}
					onAddBack={() => {
						setResult(null);
						setSaved(null);
						setForceEdit(true);
					}}
				/>
			)}
			{result && !(result.discountPreview && !result.discountPreview.error && !result.discountPreview.locked) && (
				<Modal open onClose={() => onDone()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
					<Box className={css.modal}>
						<div className={css.modalHeader}>
							<span>{result.nothingChanged ? "Agreement KYC submitted again" : "Agreement KYC submitted"}</span>
						</div>
						<div className={css.modalContent}>
							{result.nothingChanged ? (
								<p className={css.muted}>Nothing changed in your KYC. No change to your total unit cost.</p>
							) : (
								<div className={css.good}>
									Saved (version {result.kycVersion}). {result.reviewDraft?.revisionNo ? `Review draft Revision ${result.reviewDraft.revisionNo} is ready: read it and confirm by OTP.` : "Your review draft is being made."}
								</div>
							)}
							{result.discountPreview === null && !result.nothingChanged && <p className={css.muted}>No change to your total unit cost.</p>}
						</div>
						<div className={css.modalFooter}>
							<button
								type="button"
								className={`${css.btn} ${css.btnP}`}
								onClick={() => {
									successTost("Agreement KYC saved.");
									onDone();
								}}
							>
								Continue
							</button>
						</div>
					</Box>
				</Modal>
			)}
		</>
	);
}
